import queryString from 'query-string';

const parseListParam = value => {
  if (value == null || value === '') return [];
  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

/**
 * Color, width, ready to ship and product type. Sort, tab and `q` already
 * have their own URL writes.
 */
export const parseSearchPageUrlState = (
  searchParams = new URLSearchParams()
) => {
  const source = searchParams ?? new URLSearchParams();
  const params =
    typeof source.get === 'function'
      ? Object.fromEntries(source.entries())
      : { ...source };

  const colorLabels = parseListParam(params.color);
  const widthRange = parseListParam(params.widthRange)[0] ?? '';
  const [minWidth = '', maxWidth = ''] = widthRange.split('-');
  const quickshipValues = parseListParam(params.quickship);

  return {
    isReadyToShipActive: quickshipValues.includes('true'),
    minWidth,
    maxWidth,
    selectedColors: Object.fromEntries(
      colorLabels.map(label => [label.toLowerCase(), true])
    ),
    colorFacetValues: Object.fromEntries(
      colorLabels.map(label => [label.toLowerCase(), label])
    ),
    colorLabels,
    type: params.type || null,
  };
};

/** `undefined` removes a key. `pages` resets because the filter set changed. */
export const buildSearchFilterUpdates = ({
  selectedColors,
  colorFacetValues,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  type,
}) => {
  const colorLabels = Object.entries(selectedColors || {})
    .filter(([, isSelected]) => isSelected)
    .map(([label]) => colorFacetValues?.[label] || label);
  const widthRange =
    minWidth || maxWidth ? `${minWidth || ''}-${maxWidth || ''}` : undefined;

  return {
    color: colorLabels.length > 0 ? colorLabels.join(',') : undefined,
    quickship: isReadyToShipActive ? 'true' : undefined,
    widthRange,
    type: type || undefined,
    pages: undefined,
  };
};

export const mergeSearchParamUpdates = (currentParams = {}, updates = {}) =>
  Object.fromEntries(
    Object.entries({ ...currentParams, ...updates }).filter(
      ([, value]) => value != null
    )
  );

/**
 * Synchronous so a later `router.replace` (the pages reset) reads these
 * params from `window.location` instead of writing the old query back.
 */
export const replaceSearchFilterParams = updates => {
  if (typeof window === 'undefined' || !updates) return;

  const prev = queryString.parse(window.location.search);
  const merged = mergeSearchParamUpdates(prev, updates);
  const nextQuery = queryString.stringify(merged);
  const pathname = window.location.pathname.endsWith('/')
    ? window.location.pathname
    : `${window.location.pathname}/`;
  const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
  const currentUrl = `${window.location.pathname}${window.location.search}`;

  if (nextUrl === currentUrl) return;

  // null, not window.history.state. That object carries Next's __NA marker,
  // and the patched replaceState then skips notifying useSearchParams.
  window.history.replaceState(null, '', nextUrl);
};
